import type { CalculatePriceMultiRoutesAPI } from '@hero/api';
import type { VehicleTypes } from '@hero/schema';

export type DraftPriceCalcDestination = {
  lat: number;
  lng: number;
  quantity?: number;
};

export type DraftPriceCalcInput = {
  orgId?: string;
  customerId?: string;
  /** Explicit party ids (mobile carrier-app may pass these directly). */
  sellerId?: string;
  buyerId?: string;
  isInternalShipment: boolean;
  origin: { lat: number; lng: number } | null | undefined;
  destinations: DraftPriceCalcDestination[];
  vehicleType?: VehicleTypes;
  vehicleTypes?: VehicleTypes[];
  /**
   * When set, `vehicle` is sent to the resolver only if the user
   * explicitly picked a type (`loadOverrides.vehicleType` or
   * `vehicleTypes`). Auto-seeded org/client/route defaults must NOT
   * scope the lane lookup — otherwise a wrong default (e.g.
   * `truck_flatbed`) hides the client's agreed Route on the lane.
   */
  loadOverrides?: Partial<Record<'vehicleType' | 'vehicleTypes', true>>;
  /**
   * When true, returns `null` unless party context is satisfied for a
   * non-internal shipment (`orgId` + `customerId`, or explicit
   * `sellerId` + `buyerId`). Publish / agent draft managers set this.
   */
  requirePartyContext?: boolean;
};

const isValidCoord = (lat: number, lng: number) => lat !== 0 || lng !== 0;

/**
 * Single source of truth for `CalculatePriceMultiRoutesAPI` request
 * assembly. Every surface that previews order pricing (publish page,
 * agent card, mobile create, reorder) must call this — never inline
 * `locations` / `sellerId` / `buyerId` in UI managers.
 */
export function buildDraftPriceCalcRequest(
  input: DraftPriceCalcInput,
): CalculatePriceMultiRoutesAPI['req'] | null {
  const {
    orgId,
    customerId,
    sellerId: explicitSellerId,
    buyerId: explicitBuyerId,
    isInternalShipment,
    origin,
    destinations,
    vehicleType,
    vehicleTypes,
    loadOverrides,
    requirePartyContext = false,
  } = input;

  if (!origin || origin.lat === 0) return null;

  const validDests = destinations.filter(d => d && isValidCoord(d.lat, d.lng));
  if (validDests.length === 0) return null;

  const resolvedSellerId = !isInternalShipment
    ? explicitSellerId ?? orgId
    : undefined;
  const resolvedBuyerId = !isInternalShipment
    ? explicitBuyerId ?? customerId
    : undefined;

  if (
    requirePartyContext &&
    !isInternalShipment &&
    !(resolvedSellerId && resolvedBuyerId)
  ) {
    return null;
  }

  const userPickedVehicle =
    loadOverrides?.vehicleType || loadOverrides?.vehicleTypes;
  const quoteVehicle = userPickedVehicle
    ? vehicleType ?? vehicleTypes?.[0]
    : undefined;

  const request: CalculatePriceMultiRoutesAPI['req'] = {
    ...(quoteVehicle ? { vehicle: quoteVehicle } : {}),
    locations: [
      { location: { lat: origin.lat, lng: origin.lng } },
      ...validDests.map(d => ({
        location: { lat: d.lat, lng: d.lng },
        ...(d.quantity != null && d.quantity > 0 ? { quantity: d.quantity } : {}),
      })),
    ],
  };

  if (!isInternalShipment) {
    // The draft stores party ids as strings; the API type wants ObjectId.
    // Double-cast — the backend accepts the hex string on the wire.
    if (resolvedSellerId) {
      request.sellerId =
        resolvedSellerId as unknown as CalculatePriceMultiRoutesAPI['req']['sellerId'];
      // Creator org = seller on carrier-originated orders; mirrors
      // `scopePartiesForNetworkQuote` so own-route priority matches
      // publish / create.
      request.creatorId =
        resolvedSellerId as unknown as CalculatePriceMultiRoutesAPI['req']['creatorId'];
    }
    if (resolvedBuyerId) {
      request.buyerId =
        resolvedBuyerId as unknown as CalculatePriceMultiRoutesAPI['req']['buyerId'];
    }
  }

  return request;
}
